<template>
  <div class="chat-shortcuts">
    <div v-if="isOpened" class="chat-shortcuts__block">
      <vue-scroll ref="scrollBody" @handle-scroll="handleScroll">
        <div class="chat-shortcuts__list" role="list" ref="shortcutsList">
          <div
            v-for="(quick, index) in filteredQuickTexts"
            :key="index"
            :class="{ 'is-active': index === currentIndex }"
            @click="setCurrentIndex(index)"
            @dblclick="selectElement(index)"
            class="chat-shortcuts__item"
            role="button"
          >
            <span
              class="chat-shortcuts__item-text"
              v-text="textReplaced(quick.text)"
            ></span>
          </div>
          <div v-if="emptyResults" class="chat-shortcuts__item">
            <chat-multimedia-empty
              :tab="$t('multimedia.texts')"
              class="chat-shortcuts__empty"
            />
          </div>
        </div>
      </vue-scroll>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import {
  CHAT_INPUT_FOCUS,
  SHORTCUTS_MOVE_DOWN,
  SHORTCUTS_MOVE_UP,
  SHORTCUTS_SEARCH_TEXT,
  SHORTCUTS_SELECT_INDEX,
  SHORTCUTS_SELECT_TEXT,
  SHORTCUTS_TOGGLE,
} from '@/eventTypes';
import EventBus from '@/eventBus';
import DataBankMixin from '@/mixins/databank';

export default {
  mixins: [DataBankMixin],
  components: {
    ChatMultimediaEmpty: () => import('@/components/ChatMultimediaEmpty.vue'),
  },
  data() {
    return {
      searchText: '',
      currentIndex: 0,
      isOpened: false,
      scrollTop: 0,
    };
  },
  computed: {
    ...mapGetters([
      'dataBankText',
    ]),
    ...mapState({
      currentCampaign: (state) => state.currentCampaign,
    }),
    currentElement() {
      return this.filteredQuickTexts[this.currentIndex];
    },
    filteredQuickTexts() {
      const q = this.normalizeString(this.searchText).toLowerCase();

      return this.quickTexts.filter((el) => (this.searchText.length > 0
        ? this.normalizeString(el.text).toLowerCase().includes(q) : true));
    },
    emptyResults() {
      return this.filteredQuickTexts.length === 0;
    },
    currentViewport() {
      return this.scrollTop + 80;
    },
    activeCampaignId() {
      if (this.isHistory
        && this.currentCampaign
      ) {
        return this.currentCampaign;
      } if (!this.isHistory
        && this.activeConversation
        && this.activeConversation.conversation) {
        return this.activeConversation.conversation.campaign.id;
      }
      return null;
    },
    quickTexts() {
      return this.dataBankText({
        campaignId: this.activeCampaignId,
      });
    },
  },
  watch: {
    filteredQuickTexts() {
      if (this.currentIndex > this.filteredQuickTexts.length - 1) {
        if (this.emptyResults) {
          this.currentIndex = 0;
        } else {
          this.currentIndex = this.filteredQuickTexts.length - 1;
        }
      }
    },
  },
  created() {
    EventBus.$on(SHORTCUTS_MOVE_DOWN, () => {
      this.moveDown();
    });

    EventBus.$on(SHORTCUTS_MOVE_UP, () => {
      this.moveUp();
    });

    EventBus.$on(SHORTCUTS_SELECT_INDEX, () => {
      const { text } = this.currentElement;

      this.notifySelection(text);
    });

    EventBus.$on(SHORTCUTS_TOGGLE, (opened) => {
      this.toggleDisplay(opened);
    });

    EventBus.$on(SHORTCUTS_SEARCH_TEXT, (text) => {
      this.searchText = text;
    });
  },
  destroyed() {
    EventBus.$off(SHORTCUTS_MOVE_DOWN);
    EventBus.$off(SHORTCUTS_MOVE_UP);
    EventBus.$off(SHORTCUTS_SELECT_INDEX);
    EventBus.$off(SHORTCUTS_TOGGLE);
    EventBus.$off(SHORTCUTS_SEARCH_TEXT);
  },
  methods: {
    handleScroll(v) {
      this.scrollTop = v.scrollTop;
    },
    setCurrentIndex(index) {
      this.currentIndex = index;
      EventBus.$emit(CHAT_INPUT_FOCUS);
    },
    selectElement() {
      EventBus.$emit(SHORTCUTS_SELECT_INDEX);
    },
    moveDown() {
      if (this.currentIndex < this.filteredQuickTexts.length - 1) {
        this.currentIndex += 1;
      }

      this.adjustViewport();
    },
    moveUp() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1;
      }

      this.adjustViewport();
    },
    adjustViewport() {
      this.$nextTick(() => {
        const { shortcutsList } = this.$refs;

        for (let i = 0; i < shortcutsList.children.length; i += 1) {
          if (shortcutsList.children[i].classList.contains('is-active')) {
            const selectedItem = shortcutsList.children[i];
            const offset = selectedItem.offsetTop;
            let newY = -1;

            if (offset > this.currentViewport) {
              newY = offset - 80;
            }

            if (offset < this.currentViewport - 80) {
              newY = offset;
            }

            if (newY > -1) {
              this.$refs.scrollBody.scrollTo({ y: newY }, 0);
            }
          }
        }
      });
    },
    toggleDisplay(opened) {
      this.isOpened = opened;

      if (!opened) {
        this.reset();
      }
    },
    notifySelection(text) {
      EventBus.$emit(SHORTCUTS_SELECT_TEXT, {
        text: this.textReplaced(text),
        campaign: this.campaignId,
      });
      this.reset();
    },
    reset() {
      this.currentIndex = 0;
      this.scrollTop = 0;
    },
    normalizeString(str) {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },
  },
};
</script>
